import { FormTemplate } from './FormTemplate';
import { ParticipantEventLog } from './ParticipantEventLog';
import type { StudyParticipant } from './StudyParticipant';
import { User } from './User';

export enum RecurringType {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Yearly = 'Yearly'
}

export enum ExpectedRespondent {
  Proxy = 'Proxy',
  SelfReport = 'Self Report',
  Flexible = 'Flexible'
}

/**
 * Based on article from Vertabelo on managing recurring event use cases.
 * https://vertabelo.com/blog/again-and-again-managing-recurring-events-in-a-data-model/
 */
export interface ParticipantEvent {
  id: number;
  studyParticipantId: number;
  studyParticipant?: StudyParticipant;
  formTemplateId: number;
  formTemplate?: FormTemplate;
  name: string;
  description?: string;
  participantAccessible: boolean;
  internalEditable: boolean;
  allowMultipleEntries: boolean;
  expectedRespondent?: ExpectedRespondent;
  ageBands?: { minAge: number; maxAge: number }[] | null;
  startDate: Date;
  endDate?: Date;
  isFullDayEvent?: boolean;
  expireDayCount?: number;
  /**
   * Identify that the event is available to complete any time day of the event or only after the intended start time.
   */
  anyTimeDayOf: boolean;
  /**
   * Used for recurring events. if not set then not recurring.
   */
  recurringType?: RecurringType;
  /**
   * Used when recurring events get rescheduled to link past recurring
   * events and future rescheduled recurring events
   */
  parentEventId?: number | null;
  /**
   * if parentEventId, store the original startDate for the recurrence event.
   * This helps to determine which date of the original recurring event was
   * rescheduled.
   */
  originalStartDate?: Date;
  /**
   * Separation count is used to extend recurring event types to handle
   * situations like every other day, bi-weekly, by-yearly. The field signifies
   * the interval (in days, weeks, or months) before the next event instance is
   * allowed. The default value is 0 to have consecutive recurrent events or
   * null if not recurring.
   */
  separationCount?: number;
  maxOccurrences?: number;
  removedOccurrenceCounts?: number[];
  dayOfWeek?: number;
  dayOfMonth?: number;
  monthOfYear?: number;
  repeatCount?: number;
  studyTemplateEventId: number;
  createdBy: User;
  created: Date;
  exceptions?: ParticipantEventException[];
  participantEventLogs: ParticipantEventLog[];
  studyTemplateEvent?: StudyTemplateEvent;
  customDay?: number;
}

export enum StudyTemplateEventType {
  Event = 'Event',
  Section = 'Section'
}

export interface StudyTemplateEvent {
  id: number;
  type: StudyTemplateEventType;
  name: string;
  daysFromStart: number;
  duration: number;
  parentId?: number;
  parent?: StudyTemplateEvent;
  position: number;
}

export interface ParticipantEventException {
  id: number;
  eventId: number;
  isRescheduled: boolean;
  isCancelled: boolean;
  startDate: Date;
  endDate: Date;
  isFullDayEvent: boolean;
  createdBy: User;
  created: Date;
}
